/**
 * CW-SF-R2 TKT-6 — Top 10 Accounts by All-Time Closed-Won (CEO role).
 *
 * Strategic-relationship list. Rows deep-link to the M3 TKT-7 dossier
 * mode (same as D1 Top Accounts by Open Pipeline).
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface AccountBucket {
  external_file_id: string;
  account_name: string | null;
  account_url: string | null;
  total: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  accounts: AccountBucket[];
  amount_denied: boolean;
  total_visible: number;
}

const REFRESH_S = 5 * 60;

export function SfTopAccountsAllTimeWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/top-accounts-all-time', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const accounts = data?.accounts ?? [];

  return (
    <div className="sf-widget sf-widget--top-accounts">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={accounts.length > 0}
        emptyMessage={
          data?.amount_denied
            ? "Your Salesforce profile doesn't have access to Opportunity Amount."
            : 'No closed-won opportunities to rank.'
        }
      >
        <ul className="sf-widget__list">
          {accounts.map((a) => (
            <li key={a.external_file_id} className="sf-widget__row">
              <RouterLink
                to={`/modules/cw/files?provider=salesforce&parent=${encodeURIComponent(a.external_file_id)}`}
                className="sf-widget__row-name"
              >
                {a.account_name ?? '(account)'}
              </RouterLink>
              <div className="sf-widget__row-chips">
                <span className="sf-widget__chip sf-widget__chip--amount">
                  {formatCurrency(a.total)}
                </span>
                <span className="sf-widget__chip">
                  {a.count} {a.count === 1 ? 'opp' : 'opps'}
                </span>
                {a.account_url && (
                  <a
                    href={a.account_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open in Salesforce"
                    className="sf-widget__chip-link"
                  >
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
        {data && data.total_visible > 0 && (
          <div className="sf-widget__footer-meta">
            All-time closed-won visible: {formatCurrency(data.total_visible)}
          </div>
        )}
      </SfWidgetShell>
    </div>
  );
}
